import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Searchable dropdown with server-side search and infinite scroll — same look and behaviour as
 * SearchableCostCenterDropdown / SearchableLedgerDropdown (Bill Entry), for any paged list API.
 *
 * Props:
 *   value        — selected id (string)
 *   onChange     — (id: string, label: string, item) => void
 *   displayLabel — label for the selected value (edit mode)
 *   fetchPage    — async (search, page, limit) => ({ items, page, pages, total })
 *   getLabel     — (item) => string shown in the list and the button
 *   getSubLabel  — optional (item) => second, smaller line
 *   resetKey     — when it changes the cached list is dropped (e.g. a parent filter changed)
 *   noun         — 'bank' → "12 banks" in the header
 *   disabled, error, placeholder, limit, menuPlacement ('down' | 'up')
 */
const SearchablePagedDropdown = ({
  value,
  onChange,
  displayLabel = '',
  fetchPage,
  getLabel,
  getSubLabel,
  resetKey = '',
  noun = 'item',
  disabled = false,
  error = false,
  placeholder = 'Select',
  searchPlaceholder = 'Search…',
  limit = 30,
  menuPlacement = 'down'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(displayLabel);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownUidRef = useRef(null);
  const listRef = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchingRef = useRef(false);
  const currentSearchRef = useRef('');
  const requestIdRef = useRef(0);

  const computeDropdownStyle = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const up = menuPlacement === 'up' || (menuPlacement === 'auto' && spaceBelow < 260 && rect.top > spaceBelow);
    setDropdownStyle(
      up
        ? { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left, width: Math.max(rect.width, 260), zIndex: 10000 }
        : { position: 'fixed', top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 260), zIndex: 10000 }
    );
  }, [menuPlacement]);

  useEffect(() => { setSelectedLabel(displayLabel || ''); }, [displayLabel]);

  // A changed parent filter means a different list
  useEffect(() => {
    requestIdRef.current += 1;
    fetchingRef.current = false;
    setItems([]);
    setPagination({ page: 1, pages: 1, total: 0 });
  }, [resetKey]);

  const load = useCallback(async (searchTerm, page, append) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await fetchPage(searchTerm || '', page, limit);
      if (requestId !== requestIdRef.current) return;
      const next = Array.isArray(res?.items) ? res.items : [];
      setItems(prev => (append ? [...prev, ...next] : next));
      setPagination({ page: res?.page || page, pages: res?.pages || 1, total: res?.total ?? next.length });
    } catch (err) {
      console.error('SearchablePagedDropdown: fetch error', err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        fetchingRef.current = false;
      }
    }
  }, [fetchPage, limit]);

  const openDropdown = () => {
    if (disabled) return;
    computeDropdownStyle();
    setIsOpen(true);
    document.dispatchEvent(new CustomEvent('slim:dropdownOpened', { detail: dropdownUidRef }));
    if (items.length === 0 && !loading) {
      currentSearchRef.current = '';
      load('', 1, false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const focusInput = () => searchInputRef.current?.focus();
    focusInput();
    const timer = setTimeout(focusInput, 30);
    const update = () => computeDropdownStyle();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, computeDropdownStyle]);

  useEffect(() => {
    const outside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) { setIsOpen(false); setSearch(''); }
    };
    const otherOpened = (e) => { if (e.detail !== dropdownUidRef) { setIsOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', outside);
    document.addEventListener('slim:dropdownOpened', otherOpened);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('slim:dropdownOpened', otherOpened);
    };
  }, []);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearch(term);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      currentSearchRef.current = term;
      fetchingRef.current = false;
      load(term, 1, false);
    }, 300);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom && !loading && !fetchingRef.current && pagination.page < pagination.pages) {
      load(currentSearchRef.current, pagination.page + 1, true);
    }
  };

  const handleSelect = (item) => {
    const label = getLabel(item);
    setSelectedLabel(label);
    onChange(String(item._id), label, item);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    onChange('', '', null);
  };

  const hasMore = pagination.page < pagination.pages;
  const pluralNoun = noun.endsWith('h') ? `${noun}es` : `${noun}s`;
  const plural = (n) => `${n} ${n === 1 ? noun : pluralNoun}`;

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className={`w-full px-3 py-1.5 text-sm border rounded flex items-center justify-between text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
        } ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'hover:border-indigo-400 cursor-pointer'}`}
      >
        <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          {selectedLabel && !disabled && (
            <span onClick={handleClear} className="text-gray-300 hover:text-gray-500 leading-none text-sm" title="Clear selection">x</span>
          )}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div style={dropdownStyle} className="bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                autoFocus
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
              />
            </div>
            {pagination.total > 0 && <p className="text-xs text-gray-400 mt-1">{plural(pagination.total)}</p>}
          </div>

          <div ref={listRef} onScroll={handleScroll} className="max-h-52 overflow-y-auto">
            {!loading && items.length === 0 && (
              <p className="px-3 py-4 text-xs text-gray-500 text-center">
                {search ? `No ${pluralNoun} match your search` : `No ${pluralNoun} found`}
              </p>
            )}

            {items.map((item) => {
              const isSelected = String(item._id) === String(value);
              const sub = getSubLabel ? getSubLabel(item) : null;
              return (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className={`w-full flex flex-col px-3 py-1.5 text-left transition-colors ${
                    isSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xs font-medium leading-tight">{getLabel(item)}</span>
                  {sub && <span className="text-[11px] text-gray-500 leading-tight">{sub}</span>}
                </button>
              );
            })}

            {loading && (
              <div className="flex items-center justify-center py-3">
                <svg className="animate-spin w-3 h-3 text-indigo-600 mr-1.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="text-xs text-gray-500">Loading...</span>
              </div>
            )}

            {!loading && hasMore && (
              <p className="text-center text-xs text-gray-400 py-1.5 border-t border-gray-100">Scroll for more results</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchablePagedDropdown;
